import {
  html,
  attr,
  prop,
  Signal,
  When,
  Fragment,
  NotEmpty,
} from '@tempots/dom'
import { Anchor, AsyncResultView } from '@tempots/ui'
import {
  Button,
  Icon,
  Modal,
  InputWrapper,
  TextInput,
  EditableText,
} from '@tempots/beatui'
import { PageLayout } from '@/components/layout'
import { DataTable } from '@/components/data'
import { formatCredits, formatBillingMethod } from '@/utils'
import { Workspace } from '@/state/workspace'
import { organizations as orgsService } from '@/services/gateway'
import { NavigationService } from '@tempots/ui'
import { Organization } from '@/services'

// ============================================================================
// Organizations Page Component
// ============================================================================

export function OrganizationsPage() {
  // const organizations = Workspace.organizations
  // const isLoading = prop(true)
  // const error = prop<string | null>(null)

  // Form state for create modal
  const newOrgName = prop('')
  const isCreating = prop(false)
  const createError = prop<string | null>(null)

  // async function loadOrganizations() {
  //   isLoading.set(true)
  //   error.set(null)

  //   try {
  //     const response = await orgsService.listOrganizations()

  //     // Fetch member counts for each org

  //     /* eslint-disable tempots/require-async-signal-disposal -- plain array, not signal */
  //     const orgsWithCounts = await Promise.all(
  //       response.organizations.map(async (org: ApiOrganization) => {
  //         let memberCount = 1
  //         try {
  //           const members = await orgsService.listMembers(org.id)
  //           memberCount = members.members.length
  //         } catch {
  //           // Fall back to 1 if we can't get members
  //         }
  //         return {
  //           id: org.id,
  //           name: org.name,
  //           isPersonal: org.isPersonal,
  //           creditBalance: org.creditBalance,
  //           memberCount,
  //           billingMethod: org.billingMethod as 'prepaid' | 'postpaid' | 'free',
  //           createdAt: org.createdAt,
  //         }
  //       })
  //     )
  //     /* eslint-enable tempots/require-async-signal-disposal */

  //     organizations.set(orgsWithCounts)
  //   } catch (err) {
  //     error.set(
  //       err instanceof Error ? err.message : 'Failed to load organizations'
  //     )
  //   } finally {
  //     isLoading.set(false)
  //   }
  // }

  async function handleCreateOrg(close: () => void) {
    const name = newOrgName.get().trim()
    if (!name) {
      createError.set('Organization name is required')
      return
    }

    isCreating.set(true)
    createError.set(null)

    try {
      const newOrg = await orgsService.createOrganization(name)
      Workspace.addOrganization(newOrg)

      // Reset form and close modal
      newOrgName.set('')
      close()

      // Navigate to the new org
      NavigationService.navigate(`/o/${newOrg.id}`)
    } catch (err) {
      createError.set(
        err instanceof Error ? err.message : 'Failed to create organization'
      )
    } finally {
      isCreating.set(false)
    }
  }

  // Handle updating organization name
  async function handleUpdateOrgName(orgId: string, newName: string) {
    const trimmedName = newName.trim()
    if (!trimmedName) return

    try {
      Workspace.setOrganizationName(orgId, trimmedName)
      await orgsService.updateOrganization(orgId, trimmedName)
    } catch (err) {
      console.error(
        err instanceof Error
          ? err.message
          : 'Failed to update organization name'
      )
    }
  }

  return PageLayout({
    header: 'Organizations',
    content: Fragment(
      // Header with create button
      html.div(
        attr.class(
          'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'
        ),
        html.div(
          html.p(
            attr.class('text-sm text-gray-600'),
            'Manage your organizations and team workspaces'
          )
        ),
        Modal(
          {
            size: 'md',
            dismissable: true,
          },
          (open, close) =>
            html.div(
              Button(
                {
                  onClick: () => {
                    newOrgName.set('')
                    createError.set(null)
                    open({
                      header: html.h2(
                        attr.class('text-lg font-semibold text-gray-900'),
                        'Create Organization'
                      ),
                      body: html.div(
                        attr.class('space-y-4'),
                        html.p(
                          attr.class('text-sm text-gray-600'),
                          'Create a new team organization to collaborate with others.'
                        ),
                        InputWrapper({
                          label: 'Organization Name',
                          required: true,
                          content: TextInput({
                            value: newOrgName,
                            placeholder: 'My Team',
                            onInput: (v) => newOrgName.set(v),
                          }),
                          hasError: createError.map((e) => e !== null),
                          error: createError,
                        })
                      ),
                      footer: html.div(
                        attr.class('flex justify-end gap-3'),
                        Button(
                          {
                            variant: 'outline',
                            onClick: close,
                          },
                          'Cancel'
                        ),
                        Button(
                          {
                            disabled: isCreating,
                            onClick: () => handleCreateOrg(close),
                          },
                          isCreating.map((c) =>
                            c ? 'Creating...' : 'Create Organization'
                          )
                        )
                      ),
                    })
                  },
                },
                Icon({ icon: 'mdi:plus' }, attr.class('w-4 h-4 mr-2')),
                'Create Organization'
              )
            )
        )
      ),
      AsyncResultView(Workspace.organizations, {
        success: (organizations) =>
          NotEmpty(organizations, () =>
            html.div(
              attr.class(
                'bg-white rounded-xl border border-gray-200 overflow-hidden'
              ),
              DataTable({
                columns: [
                  {
                    key: 'name',
                    header: 'Organization',
                    render: (item) =>
                      html.div(
                        attr.class('flex items-center gap-3'),
                        html.div(
                          attr.class(
                            item.map((org) =>
                              org.isPersonal
                                ? 'w-10 h-10 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center'
                                : 'w-10 h-10 bg-purple-100 text-purple-600 rounded-lg flex items-center justify-center'
                            )
                          ),
                          Icon(
                            {
                              icon: item.map((org): string =>
                                org.isPersonal ? 'mdi:account' : 'mdi:domain'
                              ),
                            },
                            attr.class('w-5 h-5')
                          )
                        ),
                        html.div(
                          html.div(
                            attr.class('flex items-center gap-2'),
                            html.div(
                              attr.class('font-medium text-gray-900'),
                              EditableText({
                                value: item.map((org) => org.name),
                                onChange: (newName) => {
                                  const orgId = item.get().id
                                  Workspace.setOrganizationName(orgId, newName)
                                  handleUpdateOrgName(orgId, newName)
                                },
                                placeholder: 'Organization name',
                              })
                            ),
                            When(
                              item.map((org) => org.isPersonal),
                              () =>
                                html.span(
                                  attr.class(
                                    'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800'
                                  ),
                                  'Personal'
                                )
                            )
                          ),
                          html.p(
                            attr.class('text-sm text-gray-500'),
                            item.map((org) =>
                              org.isPersonal ? 'Your personal workspace' : ''
                            )
                          )
                        )
                      ),
                  },
                  {
                    key: 'creditBalance',
                    header: 'Credits',
                    render: (item) =>
                      html.span(
                        attr.class('text-sm font-medium text-gray-900'),
                        item.map((org) => formatCredits(org.creditBalance))
                      ),
                  },
                  {
                    key: 'billingMethod',
                    header: 'Billing',
                    render: (item) =>
                      html.span(
                        attr.class('text-sm text-gray-500'),
                        item.map((org) =>
                          formatBillingMethod(org.billingMethod)
                        )
                      ),
                  },
                  {
                    key: 'id',
                    header: '',
                    render: (item) =>
                      Anchor(
                        { href: item.map((org) => `/o/${org.id}`) },
                        attr.class(
                          'text-brand-600 hover:text-brand-700 text-sm font-medium'
                        ),
                        'Go to'
                      ),
                  },
                ],
                data: organizations,
                renderMobileCard: (org) => OrganizationCard(org),
                emptyMessage:
                  'No organizations yet. Create one to get started.',
              })
            )
          ),
      })
    ),
  })
}

// ============================================================================
// Organization Card Component (for mobile view)
// ============================================================================

function OrganizationCard(org: Signal<Organization>) {
  return html.div(
    attr.class('bg-white border border-gray-200 rounded-lg p-4'),
    html.div(
      attr.class('flex items-start justify-between'),
      html.div(
        attr.class('flex items-center gap-3'),
        html.div(
          attr.class(
            org.map((o) =>
              o.isPersonal
                ? 'w-10 h-10 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center'
                : 'w-10 h-10 bg-purple-100 text-purple-600 rounded-lg flex items-center justify-center'
            )
          ),
          Icon(
            {
              icon: org.map((o): string =>
                o.isPersonal ? 'mdi:account' : 'mdi:domain'
              ),
            },
            attr.class('w-5 h-5')
          )
        ),
        html.div(
          html.div(
            attr.class('flex items-center gap-2'),
            html.h3(
              attr.class('font-medium text-gray-900'),
              org.map((o) => o.name)
            ),
            When(
              org.map((o) => o.isPersonal),
              () =>
                html.span(
                  attr.class(
                    'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800'
                  ),
                  'Personal'
                )
            )
          ),
          html.p(
            attr.class('text-sm text-gray-500 mt-1'),
            org.map((o) => (o.isPersonal ? 'Your personal workspace' : ''))
          )
        )
      ),
      Anchor(
        { href: org.map((o) => `/o/${o.id}`) },
        attr.class('text-brand-600 hover:text-brand-700'),
        Icon({ icon: 'mdi:chevron-right' }, attr.class('w-5 h-5'))
      )
    ),
    html.div(
      attr.class('mt-3 flex items-center gap-4 text-sm text-gray-500'),
      html.span(
        attr.class('font-medium text-gray-900'),
        org.map((o) => formatCredits(o.creditBalance))
      ),
      html.span('•'),
      html.span(org.map((o) => formatBillingMethod(o.billingMethod)))
    )
  )
}
